// Domain model for the Trust Lens. Deliberately subjective: every tag is owned
// by a viewer, there is no global or aggregate score anywhere in these types.

// Present so a future multi-user layer needs no migration. The MVP has one.
export const DEFAULT_VIEWER: ViewerId = "local";
export type ViewerId = string;

export type PersonId = string;
export type DimensionId = string;
export type TagId = string;
export type DecisionId = string;

// How a person was added. "url" means a profile link was pasted and only the
// handle or name was resolved; "import" means a contacts CSV. No platform
// content or email is ever stored.
export type PersonSource = "manual" | "url" | "import";

export interface Person {
  id: PersonId;
  // "platform:handle_or_id" when resolved from a pasted link or import, else a
  // local id. A clickable profile link is reconstructed from this.
  target_key: string;
  display_name: string;
  relationship_context: string;
  // Optional contact email (e.g. from a contacts import). Kept LOCAL only and
  // never sent to the model.
  email?: string;
  source: PersonSource;
  created_at: number;
}

export interface Dimension {
  id: DimensionId;
  name: string;
  // One-line definition of what this quality means, shown beside the name so a
  // rating is calibrated against a shared sense of the quality. "" when unset.
  definition: string;
  // Display order, so the seeded qualities read in their intended sequence
  // (Judgement first) rather than by internal id.
  order: number;
  // A nullable seam for a future network phase that maps a viewer's private
  // dimension to a shared canonical quality. NO mapping logic exists now; this
  // is only persisted and round-tripped. Always null until that phase.
  canonical_key: string | null;
  // The purpose pack(s) this dimension came from (pack keys), so a large
  // multi-purpose set can be filtered by purpose. Empty for custom/proposed
  // dimensions. When two packs contribute the same-named dimension it is merged
  // and carries both keys.
  purposes: string[];
}

export interface Tag {
  id: TagId;
  viewer_id: ViewerId;
  person_id: PersonId;
  dimension_id: DimensionId;
  // A simple 1..5 rating (the manual rating; acts as the prior). The relative
  // share is computed by normalising the standing point within each dimension
  // (see rating.ts / standing.ts); no budget is shown.
  rating: number;
  rationale: string;
  // When the current read VALUE was last set (a baseline or a re-rate). Only
  // events logged at/after this anchor count toward the standing, so re-rating
  // resets the baseline. Defaults to created_at when absent.
  read_at?: number;
  created_at: number;
  // When the row was last touched (read or note). Shown as "saved <date>".
  updated_at?: number;
}

export type RatingEventId = string;

// An append-only record of a rating VALUE change (the standing's history). Note-
// only edits do not produce an event, so this stays a clean trail.
export interface RatingEvent {
  id: RatingEventId;
  viewer_id: ViewerId;
  person_id: PersonId;
  dimension_id: DimensionId;
  rating: number;
  rationale: string;
  at: number;
}

export type InstanceId = string;
export type Valence = "+" | "-";

// An optional dated piece of evidence that nudges a (person, dimension)
// standing. Magnitude 1..3 (minor / moderate / major).
export interface Instance {
  id: InstanceId;
  viewer_id: ViewerId;
  person_id: PersonId;
  dimension_id: DimensionId;
  valence: Valence;
  magnitude: number;
  note: string;
  occurred_at: number;
  created_at: number;
}

export type TopicId = string;

// A domain of expertise (e.g. "Offshore wind"). A SEPARATE axis from dimensions:
// dimensions rank (how good), domains filter (good at what). Never fused. The
// vocabulary is controlled and deduped by `key` so "Solar" and "solar" do not
// split; `key` is also the only seam a future canonicalisation would need.
export interface Topic {
  id: TopicId;
  name: string;
  key: string;
  source: "curated" | "custom";
  created_at: number;
}

export type PersonTopicId = string;

// A person carries multiple domain flags via these links. No rating, no
// dimension: a flat membership only.
export interface PersonTopic {
  id: PersonTopicId;
  person_id: PersonId;
  topic_id: TopicId;
}

// The dedup root: trim, lowercase, collapse internal whitespace. Two entries
// with the same root are the same domain.
export function normaliseTopic(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

export interface Decision {
  id: DecisionId;
  prompt: string;
  output: string;
  // The people the brief actually drew on (the relevant subset Assemble sent to
  // the model), captured at write time so the person hub can list the decisions
  // that referenced someone without fuzzy name matching. Empty on old decisions.
  referenced_person_ids: PersonId[];
  created_at: number;
}
